import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { getValueProperty, recaptchaHeaders } from '../../helpers/functions.helpers';
import { ApplicationService } from '../application/application.service';
import { LinkupService } from '../link-up/linkup.service';
import { TokenService } from '../security/token.service';

export class IClientValidationException implements Error {
  readonly name = 'ClientValidationException';
  readonly message = 'No es posible validar existencia del usuario';
}

export interface IClientValidationErrorResponse {
  code: string,
  message: string
}

export interface IRequestClientValidation {
  document_type: string,
  document_number: string,
  solicitation_application: string,
  crypto_vault?: {
    fields?: Array<string>,
    headers?: Array<string>
  }
}

export interface IResponseClientValidation {
  data: {
    client_exist: boolean,
    [key: string]: boolean
  }
}

@Injectable({
  providedIn: 'root'
})
export class ClientValidationService {

  private readonly _id = 'CLIENT_VALIDATION';

  constructor(
    private readonly _token: TokenService,
    private readonly _http: HttpClient,
    private readonly _link: LinkupService,
    public readonly _app: ApplicationService
  ) { }

  /**
 * Permite realizar la validacion de la existencia de un cliente desde canal publico
 * @param data
 * @returns
 */
  async send(data: IRequestClientValidation, component: string = 'form-solicitation-basic-data'): Promise<any> {
    try {
      const settings = await this._link.settings;
      const useCryptoVault = this._app.getSetting(getValueProperty(settings, `context.settings.${component}`), "services", "useCryptoVault", false);
      const documentType = getValueProperty(data, "document_type", "");
      const documentNumber = getValueProperty(data, "document_number", "");
      let httpOptions: any = await this.httpOptions(documentType, documentNumber);

      if(this._app.useRecaptcha && getValueProperty(settings, `request.${this._id}.recaptcha`, false)){
        const recaptchaSettings = getValueProperty(settings, `request.${this._id}.recaptcha`, { options: {}, headers: {} });
        const captchaOptions = getValueProperty(recaptchaSettings, "options", {"action": `${this._id}`});
        httpOptions = await recaptchaHeaders(this._app, httpOptions, recaptchaSettings, captchaOptions);
      }

      if (useCryptoVault) {
        const transaction_id = await this._token.transactionId;
        await this._app.initCryptoVault(transaction_id);
        const cryptoVaultSettings = getValueProperty(settings, `request.${this._id}.cryptoVault`, { fields: [], headers: [] });
        data = await this._app.encrypt(data, getValueProperty(cryptoVaultSettings, "fields", []));
        httpOptions.headers = new HttpHeaders(await this._app.encrypt(httpOptions.headers, getValueProperty(cryptoVaultSettings, "headers", []), true));
        data.crypto_vault = { ...cryptoVaultSettings };
      }
      const contextUrls = await this._link.contextUrls;
      const URL = `${getValueProperty(contextUrls, `URLS.${this._id}`)}`;
      const req$ = this._http.post<IResponseClientValidation>(URL, { data: { ...data } }, httpOptions);
      const response: any = await req$.toPromise();
      if (documentNumber == '0000000000000') { //Nombres y apellidos
        return {
          data: {
            "family_names": true,
            "gender": true,
            "city": true,
            "street": true,
            "sub_segment_id": true,
            "given_names": true,
            "department": true,
            "segment_id": true,
            "code_ciiu": true
          }
        } as any;
      } else if (documentNumber == '111111') { //Ciudad, subsegmento
        return {
          data: {
            "family_names": true,
            "gender": true,
            "city": false,
            "street": true,
            "sub_segment_id": false,
            "given_names": true,
            "department": true,
            "segment_id": true,
            "code_ciiu": true
          }
        } as any;
      } else if (documentNumber == '222222') { //Genero
        return {
          data: {
            "family_names": true,
            "gender": false,
            "city": true,
            "street": false,
            "sub_segment_id": true,
            "given_names": true,
            "department": false,
            "segment_id": false,
            "code_ciiu": false
          }
        } as any;
      } else {
        return response;
      }

    } catch (error) {
      const status = getValueProperty(error, 'status', 500);
      if (status === 403) {
        return await this.send(data, component);
      } else {
        return {
          code: 'E002',
          message: 'Error al intentar consultar la existencia del cliente'
        } as IClientValidationErrorResponse;
      }
    }

  }

  /**
   * Permite construir de manera dinamica los encabezados para las peticiones al servicio de consulta
   */
  async httpOptions(documentType: string, documentNumber: string) {
    const { accessToken } = await this._token.accessToken;
    const transaction_id = await this._token.transactionId;
    const session_id = await this._token.sessionId;
    return {
      headers: new HttpHeaders()
        .set('Authorization', `Bearer ${accessToken}`)
        .set('token-client-id', `${documentNumber}_${documentType}`)
        .set('application', this._app.application)
        .set('channel-id', this._app.channelId)
        .set('session_id', session_id)
        .set('terminal_id', '127.0.0.1')
        .set('timestamp', new Date().toISOString())
        .set('transaction_id', transaction_id)
    };
  }

}
